/**
 * Posts a message to a Slack channel via an Incoming Webhook.
 *
 * The webhook URL is configured through `BIG_FLIP_SLACK_WEBHOOK_URL`.
 * When it is not set (e.g. local dev), the call is a no-op so we don't
 * spam the channel with noise from development environments.
 *
 * Note: we intentionally avoid setting a `Content-Type: application/json`
 * header. Doing so would turn the request into a CORS "preflighted" request,
 * which Slack's webhook endpoint rejects from the browser. Sending the JSON
 * body without custom headers keeps it a "simple request" (text/plain) that
 * Slack still parses correctly.
 *
 * @see https://api.slack.com/messaging/webhooks
 */
export async function sendSlackWebhook(payload: Record<string, unknown>): Promise<void> {
  const webhookUrl = process.env.BIG_FLIP_SLACK_WEBHOOK_URL;

  if (!webhookUrl) {
    return;
  }

  try {
    await fetch(webhookUrl, {
      method: "POST",
      body: JSON.stringify(payload)
    });
  } catch {
    // Swallow errors: notifying Slack must never break the user-facing flow.
  }
}

interface SlackErrorReport {
  error?: { message?: string };
  /** Where the error happened, e.g. the page URL. */
  source?: string;
}

interface SlackSchemaMismatchReport {
  /** Endpoint label, e.g. "GET /pwf/payment-history". */
  endpoint: string;
  /** Pre-formatted issue summaries (paths + expected types; no response values). */
  issues: string[];
}

/**
 * Sends a formatted "API response didn't match its valibot schema" report to
 * the configured Slack channel. Fired by `safeParseResponse` when a response
 * fundamentally doesn't match and the UI degrades to its fallback value.
 */
export async function sendSlackSchemaMismatchReport({ endpoint, issues }: SlackSchemaMismatchReport): Promise<void> {
  const environment = process.env.NODE_ENV;
  const gitSha = process.env.BIG_FLIP_GIT_SHA;
  const source = typeof window !== "undefined" ? window.location.href : undefined;

  const text = [
    ":warning: *Big Flip API schema mismatch*",
    `*Environment:* ${environment ?? "unknown"}`,
    gitSha ? `*Release:* ${gitSha}` : null,
    source ? `*Location:* ${source}` : null,
    `*Endpoint:* ${endpoint}`,
    issues.length ? `*Issues:*\n${issues.map((issue) => `• ${issue}`).join("\n")}` : null
  ]
    .filter(Boolean)
    .join("\n");

  await sendSlackWebhook({ text, unfurl_links: false, unfurl_media: false });
}

/**
 * Sends a formatted crash report to the configured Slack channel.
 */
export async function sendSlackErrorReport({ error, source }: SlackErrorReport): Promise<void> {
  const environment = process.env.NODE_ENV;
  const gitSha = process.env.BIG_FLIP_GIT_SHA;

  const text = [
    ":rotating_light: *Big Flip frontend crash*",
    `*Environment:* ${environment ?? "unknown"}`,
    gitSha ? `*Release:* ${gitSha}` : null,
    source ? `*Location:* ${source}` : null,
    `*Error:* ${error?.message ?? "Unknown error"}`
  ]
    .filter(Boolean)
    .join("\n");

  await sendSlackWebhook({ text, unfurl_links: false, unfurl_media: false });
}
